import React, { useMemo, useEffect, useState, useCallback } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { getThemeById } from '../../config/themes'
import type { TenantThemeConfig } from '../../types/theme'
import * as authService from '../../services/authService'
import {
  USER_SUMMARY_UPDATED_EVENT,
  getActivePointsRules,
  getActiveRewards,
  getPointsConfig,
  type PointsConfigData,
  type PointsReward,
  type PointsRule,
} from '../../services/pointsService'
import { usePredictionUserEntry } from '../../hooks/usePredictionUserEntry'
import { PointsPredictionIcon, PointsRewardIcon } from '../../components/points/PointsTabIcons'
import './pointsLayout.css'

interface AssetConfig {
  CDN_BASE_URL: string
  CSS_PATH: string
  IMG_PATH: string
  FONTS_PATH: string
}

export interface PointsOutletContext {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
  getAssetUrl: (path: string) => string
  pointsText: string
  pointsConfig: PointsConfigData | null
  activeCashToPointsRules: PointsRule[]
  activePointsToCashRules: PointsRule[]
  activeRewards: PointsReward[]
  refreshUserSummary: () => Promise<void>
}

interface ModernPointsLayoutProps {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
}

type PointsTab = {
  to: string
  sub: string
  label: string
  icon?: string
  Icon?: React.ComponentType<{ className?: string }>
}

const BASE_TABS: PointsTab[] = [
  { to: 'moneytopoint', icon: '/img/deposit-icon.png', sub: 'แลกเปลี่ยน', label: 'เงินเป็นคะแนน' },
  { to: 'pointtomoney', icon: '/img/withdraw-icon.png', sub: 'แลกเปลี่ยน', label: 'คะแนนเป็นเงิน' },
  { to: 'rewards', Icon: PointsRewardIcon, sub: 'แลกรางวัล', label: 'ของรางวัล' },
  { to: 'reward-history', icon: '/img/credit-icon.png', sub: 'ประวัติ', label: 'แลกรางวัล' },
]

const PREDICTION_TAB: PointsTab = {
  to: 'games/prediction',
  Icon: PointsPredictionIcon,
  sub: 'เกมทายผล',
  label: 'Prediction',
}

/** Layout หน้าคะแนน (points): หัวข้อ + สรุปคะแนน + แท็บ 2 อัน (เงินเป็นคะแนน, คะแนนเป็นเงิน) + Outlet */
const ModernPointsLayout: React.FC<ModernPointsLayoutProps> = ({ themeConfig, assetConfig }) => {
  const [userSummary, setUserSummary] = useState<authService.UserInfoSummary | null>(null)
  const [pointsConfig, setPointsConfig] = useState<PointsConfigData | null>(null)

  const themeFolder = useMemo(() => getThemeById(themeConfig.themeId)?.folder ?? 'HuayToday-Blue', [themeConfig.themeId])
  const { showPredictionEntry } = usePredictionUserEntry(themeConfig.settings.predictionEnabled)
  const tabs = useMemo(() => {
    if (showPredictionEntry) {
      return [...BASE_TABS, PREDICTION_TAB]
    }
    return BASE_TABS
  }, [showPredictionEntry])
  const getAssetUrl = (path: string) => {
    const base = assetConfig.CDN_BASE_URL ? `${assetConfig.CDN_BASE_URL}/${themeFolder}` : `/${themeFolder}`
    return base + (path.startsWith('/') ? path : `/${path}`)
  }

  const refreshUserSummary = useCallback(async () => {
    if (!authService.isAuthenticated()) {
      setUserSummary(null)
      return
    }
    const summary = await authService.fetchUserInfoSummary({ force: true })
    setUserSummary(summary)
  }, [])

  useEffect(() => {
    void refreshUserSummary()
  }, [refreshUserSummary])

  useEffect(() => {
    const tenantId = themeConfig.tenantId
    if (!tenantId || !authService.isAuthenticated()) {
      setPointsConfig(null)
      return
    }
    getPointsConfig(tenantId).then(setPointsConfig)
  }, [themeConfig.tenantId])

  useEffect(() => {
    const handleUserSummaryUpdated = () => {
      void refreshUserSummary()
    }
    window.addEventListener(USER_SUMMARY_UPDATED_EVENT, handleUserSummaryUpdated)
    return () => window.removeEventListener(USER_SUMMARY_UPDATED_EVENT, handleUserSummaryUpdated)
  }, [refreshUserSummary])

  const pointsText = userSummary?.points != null ? `${Number(userSummary.points).toFixed(2)} Pts` : '0.00 Pts'
  const context: PointsOutletContext = {
    themeConfig,
    assetConfig,
    getAssetUrl,
    pointsText,
    pointsConfig,
    activeCashToPointsRules: getActivePointsRules(pointsConfig?.cashToPointsRules),
    activePointsToCashRules: getActivePointsRules(pointsConfig?.pointsToCashRules),
    activeRewards: getActiveRewards(pointsConfig?.rewards),
    refreshUserSummary,
  }

  return (
    <div className="content">
      <div className="container">
        <div className="row">
          <div className="col-12 col-lg-10 offset-lg-1">
            <div className="c_quinary text-center">
              <h3 className="c_tertiary font_md">คะแนนของคุณ</h3>
            </div>

            <div className="p-3 p-md-4 mb-3">
              <div className="p-2 mb-3">
                <h2 className="text-center mb-0">
                  <span className="bg_secondary c_primary border_5 font_md px-2">{pointsText}</span>
                </h2>
              </div>
              <p className="c_primary text-center mb-0">
                หมายเหตุ :{' '}
                <span className="c_tertiary">
                  ขอสงวนสิทธิ์ในการเปลี่ยนแปลงอัตราหรือ ปิดระบบรับแลกเปลี่ยนคะแนน โดยมิต้องแจ้งให้ทราบล่วงหน้า.
                </span>
              </p>
            </div>

            <div className="nav nav-pills nav-pills-main mt-3 mb-3" id="v-pills-tab" role="tablist">
              {tabs.map((tab) => (
                <NavLink
                  key={tab.to}
                  to={tab.to}
                  end={tab.to === 'moneytopoint'}
                  className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
                >
                  <div className="box_row gap-1 gap-md-2 justify-content-center">
                    <div className="icon_cycle">
                      {tab.Icon ? (
                        <tab.Icon className="points-tab-icon" />
                      ) : (
                        <img src={getAssetUrl(tab.icon || '/img/logo-m.png')} alt="" />
                      )}
                    </div>
                    <div className="text-center text-md-start">
                      <span>{tab.sub}</span>
                      <h6 className="font_md mb-0">{tab.label}</h6>
                    </div>
                  </div>
                </NavLink>
              ))}
            </div>
            <Outlet context={context} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default ModernPointsLayout
